"use client"

import { MultipleChoiceBlockContent, Theme, defaultTheme, getPlainText } from "@/types"
import { useState, useEffect } from "react"
import { BlockImage } from "../BlockImage"
import { RichTextRenderer } from "../RichTextRenderer"

interface MultipleChoiceBlockProps {
  content: MultipleChoiceBlockContent
  onAnswer?: (answer: string | string[]) => void
  answer?: string | string[]
  onAutoAdvance?: () => void // New prop for auto-advancing
  theme?: Theme | null
  previewMode?: "desktop" | "mobile"
}

export function MultipleChoiceBlock({ content, onAnswer, answer, onAutoAdvance, theme = null }: MultipleChoiceBlockProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  
  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme

  // Initialize with existing answer if provided
  useEffect(() => {
    if (answer) {
      setSelectedOptions(Array.isArray(answer) ? answer : [answer])
    }
  }, [answer])

  const handleOptionChange = (option: string) => {
    let newSelection: string[]
    
    if (content.allowMultiple) {
      newSelection = selectedOptions.includes(option) 
        ? selectedOptions.filter(opt => opt !== option)
        : [...selectedOptions, option]
    } else {
      newSelection = [option]
    }
    
    setSelectedOptions(newSelection)
    onAnswer?.(content.allowMultiple ? newSelection : newSelection[0])
    
    // Auto-advance for single choice questions
    if (!content.allowMultiple && newSelection.length > 0) {
      setTimeout(() => {
        onAutoAdvance?.()
      }, 300) // Small delay for visual feedback
    }
  }

  const renderContent = () => {
    const questionElement = (
      <div className="mb-3 text-center">
        <h3 
          className="text-2xl font-semibold"
          style={{ 
            color: activeTheme.colors.text,
            fontFamily: activeTheme.fonts.heading
          }}
        >
          <RichTextRenderer content={content.question} />
        </h3>
        {content.subtitle && (
          <div 
            className="text-lg mt-1 opacity-80"
            style={{ 
              color: activeTheme.colors.text,
              fontFamily: activeTheme.fonts.body
            }}
          >
            <RichTextRenderer content={content.subtitle} />
          </div>
        )}
      </div>
    )

    const optionsElement = (
      <div className="flex flex-col items-center" style={{ gap: 0 }}>
        {content.options?.map((option, index) => {
          const optionText = getPlainText(option)
          const isSelected = selectedOptions.includes(optionText)
          return (
            <div 
              key={index}
              style={{
                marginTop: index === 0 ? '0' : '0.375rem',
                marginBottom: '0',
                width: '100%',
                maxWidth: '20rem',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <button
                onClick={() => handleOptionChange(optionText)}
                className={`w-full p-4 text-center rounded-full border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isSelected
                    ? "shadow-lg transform scale-[1.02]"
                    : "hover:shadow-md"
                }`}
                style={{
                  borderColor: isSelected ? activeTheme.colors.primary : activeTheme.colors.primary,
                  backgroundColor: isSelected ? `${activeTheme.colors.primary}20` : activeTheme.colors.primary,
                  color: isSelected ? activeTheme.colors.primary : activeTheme.colors.buttonText,
                  '--tw-ring-color': activeTheme.colors.primary,
                  fontFamily: activeTheme.fonts.body,
                  margin: '0'
                } as React.CSSProperties}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = activeTheme.colors.secondary || activeTheme.colors.primary
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = activeTheme.colors.primary
                  }
                }}
              >
                <div className="flex items-center justify-center">
                  <span className="text-lg font-medium">{optionText}</span>
                  {content.allowMultiple && (
                    <div className="flex-shrink-0 ml-3">
                      <div 
                        className="w-5 h-5 rounded-full border-2 transition-all duration-200"
                        style={{
                          backgroundColor: isSelected ? activeTheme.colors.primary : 'transparent',
                          borderColor: isSelected ? 'transparent' : activeTheme.colors.buttonText
                        }}
                      />
                    </div>
                  )}
                </div>
              </button>
            </div>
          )
        })}
      </div>
    )

    return (
      <>
        {questionElement}
        {optionsElement}
      </>
    )
  }

  return (
    <div>
      {renderContent()}
    </div>
  )
}
